import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy } from 'lucide-react';
import { employeeCreateSchema, type EmployeeDetail } from '@workforce/shared';
import { ApiError, api } from '@/lib/api';
import { useCalendars, useDepartments, useEmployeeOptions, useReference, useSchedules } from '@/lib/queries';
import { formatMinutes, t } from '@/lib/i18n';
import { useLeavePolicies } from '@/lib/leave';
import { CountrySelect } from '@/components/CountrySelect';
import { TimeZoneSelect } from '@/components/TimeZoneSelect';
import { Button, Card, ErrorBanner, Field, Input, Modal, Notice, PageHeader, Select, Spinner, Toggle } from '@/components/ui';

interface FormState {
  employeeCode: string;
  fullName: string;
  email: string;
  phone: string;
  countryCode: string;
  city: string;
  departmentId: string;
  position: string;
  managerId: string;
  employmentType: string;
  employmentStatus: string;
  startDate: string;
  endDate: string;
  canApproveLeave: boolean;
  attendanceRequired: boolean;
  hoursSource: 'attendance' | 'computer';
  trackWhen: '' | 'clocked_in' | 'schedule' | 'always';
  payrollMode: 'standard' | 'full' | 'hourly';
  workArrangement: string;
  officeName: string;
  attendancePin: string;
  dateOfBirth: string;
  priorExperienceYears: string;
  hasDisability: boolean;
  // create-only
  timezone: string;
  workScheduleId: string;
  holidayCalendarId: string;
  leavePolicyId: string;
  createAccount: boolean;
  accountRole: string;
  reason: string;
}

const EMPTY: FormState = {
  employeeCode: '',
  fullName: '',
  email: '',
  phone: '',
  countryCode: '',
  city: '',
  departmentId: '',
  position: '',
  managerId: '',
  employmentType: 'full_time',
  employmentStatus: 'active',
  startDate: new Date().toISOString().slice(0, 10),
  endDate: '',
  canApproveLeave: false,
  attendanceRequired: true,
  hoursSource: 'attendance',
  trackWhen: '',
  payrollMode: 'standard',
  workArrangement: 'office',
  officeName: '',
  attendancePin: '',
  dateOfBirth: '',
  priorExperienceYears: '0',
  hasDisability: false,
  timezone: '',
  workScheduleId: '',
  holidayCalendarId: '',
  leavePolicyId: '',
  createAccount: true,
  accountRole: 'employee',
  reason: '',
};

export function EmployeeFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const ref = useReference();
  const deps = useDepartments();
  const schedules = useSchedules();
  const calendars = useCalendars();
  const managers = useEmployeeOptions();
  const policies = useLeavePolicies();
  const [f, setF] = useState<FormState>(EMPTY);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<{ id: string; name: string; password: string | null } | null>(null);

  const existing = useQuery({
    queryKey: ['employee', id],
    queryFn: () => api.get<EmployeeDetail>(`/employees/${id}`),
    enabled: editing,
  });

  useEffect(() => {
    const e = existing.data;
    if (!e) return;
    setF({
      ...EMPTY,
      employeeCode: e.employeeCode,
      fullName: e.fullName,
      email: e.email,
      phone: e.phone ?? '',
      countryCode: e.countryCode,
      city: e.city ?? '',
      departmentId: e.departmentId ?? '',
      position: e.position ?? '',
      managerId: e.managerId ?? '',
      employmentType: e.employmentType,
      employmentStatus: e.employmentStatus,
      startDate: e.startDate,
      endDate: e.endDate ?? '',
      canApproveLeave: e.canApproveLeave,
      attendanceRequired: e.settings.attendanceRequired,
      hoursSource: e.settings.hoursSource ?? 'attendance',
      trackWhen: e.settings.trackWhen ?? '',
      payrollMode: e.settings.payrollMode ?? 'standard',
      workArrangement: e.workArrangement,
      officeName: e.officeName ?? '',
      attendancePin: e.attendancePin ?? '',
      dateOfBirth: e.leaveFacts?.dateOfBirth ?? '',
      priorExperienceYears: String(e.leaveFacts?.priorExperienceYears ?? 0),
      hasDisability: e.leaveFacts?.hasDisability ?? false,
    });
  }, [existing.data]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const profile = {
        employeeCode: f.employeeCode.trim(),
        fullName: f.fullName.trim(),
        email: f.email.trim(),
        phone: f.phone,
        countryCode: f.countryCode,
        city: f.city,
        departmentId: f.departmentId || null,
        position: f.position,
        managerId: f.managerId || null,
        employmentType: f.employmentType,
        startDate: f.startDate,
        endDate: f.endDate || null,
        canApproveLeave: f.canApproveLeave,
        workArrangement: f.workArrangement,
        officeName: f.workArrangement === 'remote' ? '' : f.officeName,
        attendancePin: f.attendancePin.trim() || null,
        dateOfBirth: f.dateOfBirth || null,
        priorExperienceYears: Number(f.priorExperienceYears || 0),
        hasDisability: f.hasDisability,
      };
      if (editing) {
        const saved = await api.patch<EmployeeDetail>(`/employees/${id}`, {
          ...profile,
          employmentStatus: f.employmentStatus,
          reason: f.reason || undefined,
        });
        const changed: Record<string, unknown> = {};
        if (saved.settings.attendanceRequired !== f.attendanceRequired) changed.attendanceRequired = f.attendanceRequired;
        if (saved.settings.hoursSource !== f.hoursSource) changed.hoursSource = f.hoursSource;
        if ((saved.settings.trackWhen ?? '') !== f.trackWhen) changed.trackWhen = f.trackWhen || null;
        if ((saved.settings.payrollMode ?? 'standard') !== f.payrollMode) changed.payrollMode = f.payrollMode;
        if (Object.keys(changed).length) return api.put<EmployeeDetail>(`/employees/${id}/settings`, changed);
        return saved;
      }
      const payload = {
        ...profile,
        employmentStatus: f.employmentStatus,
        workTerms: {
          timezone: f.timezone,
          workScheduleId: f.workScheduleId,
          holidayCalendarId: f.holidayCalendarId || null,
          leavePolicyId: f.leavePolicyId || null,
        },
        account: f.createAccount ? { role: f.accountRole } : null,
      };
      const check = employeeCreateSchema.safeParse(payload);
      if (!check.success) {
        const errs: Record<string, string> = {};
        for (const i of check.error.issues) errs[i.path.join('.')] ??= i.message;
        setClientErrors(errs);
        throw new ApiError(400, 'bad_request', t('Please fix the highlighted fields'));
      }
      setClientErrors({});
      const created = await api.post<{ employee: EmployeeDetail; temporaryPassword: string | null }>('/employees', payload);
      if (!f.attendanceRequired || f.hoursSource !== 'attendance' || f.trackWhen || f.payrollMode !== 'standard') {
        await api.put(`/employees/${created.employee.id}/settings`, {
          ...(!f.attendanceRequired && { attendanceRequired: false }),
          ...(f.hoursSource !== 'attendance' && { hoursSource: f.hoursSource }),
          ...(f.trackWhen && { trackWhen: f.trackWhen }),
          ...(f.payrollMode !== 'standard' && { payrollMode: f.payrollMode }),
        });
      }
      return created;
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['employees'] });
      if (editing) {
        void qc.invalidateQueries({ queryKey: ['employee', id] });
        nav(`/employees/${id}`);
      } else {
        const r = res as { employee: EmployeeDetail; temporaryPassword: string | null };
        setCreated({ id: r.employee.id, name: r.employee.fullName, password: r.temporaryPassword });
      }
    },
  });

  const serverErrors = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const err = (k: string) => clientErrors[k] ?? serverErrors[k];

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  if (editing && existing.isLoading) return <Spinner />;
  const selectedSchedule = schedules.data?.find((s) => s.id === f.workScheduleId);

  return (
    <div className="mx-auto max-w-4xl">
      <Link to={editing ? `/employees/${id}` : '/employees'} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> {t('Back')}
      </Link>
      <PageHeader title={editing ? (existing.data?.fullName ? t('Edit {name}', { name: existing.data.fullName }) : t('Edit employee')) : t('Add employee')} />
      <form onSubmit={submit} className="space-y-6" noValidate>
        <ErrorBanner error={save.error} />

        <Card title={t('Personal & contact')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('Full name')} required error={err('fullName')}>
              <Input value={f.fullName} onChange={(e) => set('fullName', e.target.value)} invalid={!!err('fullName')} />
            </Field>
            <Field label={t('Employee ID')} required error={err('employeeCode')} hint={t('Your internal ID, e.g. EMP-012')}>
              <Input value={f.employeeCode} onChange={(e) => set('employeeCode', e.target.value)} invalid={!!err('employeeCode')} />
            </Field>
            <Field label={t('E-mail')} required error={err('email')} hint={t('Also used to sign in')}>
              <Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} invalid={!!err('email')} />
            </Field>
            <Field label={t('Phone')} error={err('phone')}>
              <Input type="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
            </Field>
            <Field label={t('Country')} required error={err('countryCode')}>
              <CountrySelect value={f.countryCode} onChange={(v) => set('countryCode', v)} invalid={!!err('countryCode')} />
            </Field>
            <Field label={t('City')} error={err('city')}>
              <Input value={f.city} onChange={(e) => set('city', e.target.value)} />
            </Field>
          </div>
        </Card>

        <Card title={t('Job')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('Department')} error={err('departmentId')}>
              <Select value={f.departmentId} onChange={(e) => set('departmentId', e.target.value)}>
                <option value="">{t('No department')}</option>
                {deps.data?.filter((d) => d.isActive || d.id === f.departmentId).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('Position')} error={err('position')}>
              <Input value={f.position} onChange={(e) => set('position', e.target.value)} />
            </Field>
            <Field label={t('Manager / supervisor')} error={err('managerId')}>
              <Select value={f.managerId} onChange={(e) => set('managerId', e.target.value)}>
                <option value="">{t('No manager')}</option>
                {managers.data
                  ?.filter((m) => m.id !== id)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.fullName} ({m.employeeCode})
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label={t('Employment type')} required error={err('employmentType')}>
              <Select value={f.employmentType} onChange={(e) => set('employmentType', e.target.value)}>
                {ref.data?.employmentTypes.map((et) => (
                  <option key={et.value} value={et.value}>
                    {t(et.label)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('Employment start date')} required error={err('startDate')}>
              <Input type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} invalid={!!err('startDate')} />
            </Field>
            <Field label={t('Employment status')} error={err('employmentStatus')}>
              <Select value={f.employmentStatus} onChange={(e) => set('employmentStatus', e.target.value)} disabled={f.employmentStatus === 'terminated'}>
                {ref.data?.employmentStatuses
                  .filter((s) => s.value !== 'terminated' || f.employmentStatus === 'terminated')
                  .map((s) => (
                    <option key={s.value} value={s.value}>
                      {t(s.label)}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label={t('Work arrangement')} error={err('workArrangement')} hint={t('Controls who may use the app Clock-in button (Settings → Attendance). Remote staff can instead be paid on computer hours (below).')}>
              <Select value={f.workArrangement} onChange={(e) => set('workArrangement', e.target.value)}>
                {ref.data?.workArrangements.map((w) => (
                  <option key={w.value} value={w.value}>
                    {t(w.label)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('Office')} error={err('officeName')} hint={t('e.g. Cairo office')}>
              <Input value={f.officeName} onChange={(e) => set('officeName', e.target.value)} disabled={f.workArrangement === 'remote'} />
            </Field>
            <Field label={t('Fingerprint machine ID')} error={err('attendancePin')} hint={t('The user number enrolled on the office fingerprint machine. Leave empty to match by employee ID.')}>
              <Input value={f.attendancePin} onChange={(e) => set('attendancePin', e.target.value)} inputMode="numeric" maxLength={32} />
            </Field>
            <div className="rounded-lg border border-slate-200 p-3 sm:col-span-2">
              <p className="mb-1 text-sm font-semibold text-slate-800">{t('For the annual leave rules')}</p>
              <p className="mb-3 text-xs text-slate-500">
                {t('Egyptian law: 21 days from the second year, 30 days after 10 years of experience or from age 50, 45 days with a disability. Visible to administrators only.')}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label={t('Date of birth')} error={err('dateOfBirth')}>
                  <Input type="date" value={f.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
                </Field>
                <Field label={t('Years of experience before joining')} error={err('priorExperienceYears')} hint={t('With any employer')}>
                  <Input type="number" min={0} max={60} step="0.5" value={f.priorExperienceYears} onChange={(e) => set('priorExperienceYears', e.target.value)} />
                </Field>
                <div className="pt-6">
                  <Toggle checked={f.hasDisability} onChange={(v) => set('hasDisability', v)} label={t('Has a disability')} />
                </div>
              </div>
            </div>
            {editing && (
              <Field label={t('Contract end date')} error={err('endDate')} hint={t('Leave empty for open-ended employment')}>
                <Input type="date" value={f.endDate} onChange={(e) => set('endDate', e.target.value)} />
              </Field>
            )}
            <div className="sm:col-span-2">
              <Toggle
                checked={f.canApproveLeave}
                onChange={(v) => set('canApproveLeave', v)}
                label={t('Can approve leave for their team')}
                description={t('Managers can review requests from employees who report to them only if this is on.')}
              />
            </div>
            <div className="sm:col-span-2">
              <Toggle
                checked={f.attendanceRequired}
                onChange={(v) => set('attendanceRequired', v)}
                label={t('Working hours are tracked')}
                description={t('Turn off for people whose hours are not recorded (e.g. the owner). Their days show as “Not tracked” instead of “Absent” and payroll pays the fixed salary.')}
              />
            </div>
            <Field label={t('Pay calculation')} hint={t('How payroll works out this person’s salary.')}>
              <Select value={f.payrollMode} onChange={(e) => set('payrollMode', e.target.value as FormState['payrollMode'])} aria-label={t('Pay calculation')}>
                <option value="standard">{t('Standard — full salary for the required hours, less a share for any missing hours')}</option>
                <option value="full">{t('Always full salary — never reduced for hours (e.g. a manager)')}</option>
                <option value="hourly">{t('Per hour — paid only for the hours worked (e.g. casual / short-term)')}</option>
              </Select>
            </Field>
            {f.attendanceRequired && (
              <>
                <Field label={t('Hours come from')}>
                  <Select value={f.hoursSource} onChange={(e) => set('hoursSource', e.target.value as FormState['hoursSource'])} aria-label={t('Hours come from')}>
                    <option value="attendance">{t('Clock in / out (fingerprint, door QR, app)')}</option>
                    <option value="computer">{t('Computer activity — desktop agent, no clocking in')}</option>
                  </Select>
                </Field>
                <Field label={t('The desktop agent records')} hint={f.hoursSource === 'computer' ? t('For remote staff paid on computer hours, “whenever in use” is the default.') : undefined}>
                  <Select value={f.trackWhen} onChange={(e) => set('trackWhen', e.target.value as FormState['trackWhen'])} aria-label={t('The desktop agent records')}>
                    <option value="">{f.hoursSource === 'computer' ? t('Whenever the computer is in use (default)') : t('As in company settings')}</option>
                    <option value="clocked_in">{t('Only while clocked in')}</option>
                    <option value="schedule">{t('During scheduled working hours')}</option>
                    <option value="always">{t('Whenever the computer is in use')}</option>
                  </Select>
                </Field>
                {f.hoursSource === 'computer' && (
                  <p className="text-xs text-slate-500 sm:col-span-2">
                    {t("Worked time = computer activity as set in Settings → Attendance (“Hours from computer activity count”; default: time in use, idle excluded). First and last activity replace clock-in and clock-out. Payroll, overtime and missing hours use these hours. The employee's notice tells them this before anything is recorded.")}
                  </p>
                )}
              </>
            )}
          </div>
        </Card>

        {!editing && (
          <Card title={t('Schedule, time zone & holidays')}>
            <Notice>
              {t('These settings drive leave and attendance calculations. They are effective-dated: later changes are made from the employee page with an effective date, so past periods keep the rules that applied at the time.')}
            </Notice>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t('Time zone')} required error={err('workTerms.timezone')} className="sm:col-span-2" hint={t('IANA time zone — handles daylight saving automatically.')}>
                <TimeZoneSelect value={f.timezone} onChange={(v) => set('timezone', v)} invalid={!!err('workTerms.timezone')} />
              </Field>
              <Field
                label={t('Work schedule')}
                required
                error={err('workTerms.workScheduleId')}
                hint={selectedSchedule ? t('{duration} per week', { duration: formatMinutes(selectedSchedule.weeklyMinutes) }) : undefined}
              >
                <Select value={f.workScheduleId} onChange={(e) => set('workScheduleId', e.target.value)} invalid={!!err('workTerms.workScheduleId')}>
                  <option value="">{t('Select schedule…')}</option>
                  {schedules.data?.filter((s) => s.isActive).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('Public holiday calendar')} error={err('workTerms.holidayCalendarId')}>
                <Select value={f.holidayCalendarId} onChange={(e) => set('holidayCalendarId', e.target.value)}>
                  <option value="">{t('No holiday calendar')}</option>
                  {calendars.data?.filter((c) => c.isActive).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
              <Field label={t('Leave policy')} error={err('workTerms.leavePolicyId')} className="mt-4" hint={t('Sets the yearly allowances (e.g. 21 vacation days). Individual allowances can be changed later on the Vacation Balances page.')}>
                <Select value={f.leavePolicyId} onChange={(e) => set('leavePolicyId', e.target.value)}>
                  <option value="">{t('No leave policy')}</option>
                  {policies.data?.filter((p) => p.isActive).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
          </Card>
        )}

        {!editing && (
          <Card title={t('Login account')}>
            <Toggle
              checked={f.createAccount}
              onChange={(v) => set('createAccount', v)}
              label={t('Create a login for this employee')}
              description={t('A temporary password is generated and shown once. The employee must change it at first sign-in.')}
            />
            {f.createAccount && (
              <Field label={t('Role')} className="mt-4 max-w-xs">
                <Select value={f.accountRole} onChange={(e) => set('accountRole', e.target.value)}>
                  <option value="employee">{t('Employee')}</option>
                  <option value="manager">{t('Manager')}</option>
                  <option value="admin">{t('Administrator')}</option>
                </Select>
              </Field>
            )}
          </Card>
        )}

        {editing && (
          <Card title={t('Reason for change (optional)')}>
            <Input value={f.reason} onChange={(e) => set('reason', e.target.value)} placeholder={t('Recorded in the audit log')} maxLength={500} />
          </Card>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => nav(-1)}>
            {t('Cancel')}
          </Button>
          <Button type="submit" loading={save.isPending}>
            {editing ? t('Save changes') : t('Create employee')}
          </Button>
        </div>
      </form>

      <Modal
        open={!!created}
        onClose={() => created && nav(`/employees/${created.id}`)}
        title={t('Employee created')}
        size="sm"
        footer={<Button onClick={() => created && nav(`/employees/${created.id}`)}>{t('Open employee')}</Button>}
      >
        {created?.password ? <TempPassword name={created.name} password={created.password} /> : <p className="text-sm">{t('{name} was added.', { name: created?.name })}</p>}
      </Modal>
    </div>
  );
}

export function TempPassword({ name, password }: { name: string; password: string }) {
  const [copied, setCopied] = useState(false);
  // Whole sentence with the name in bold, in the right word order for each language.
  const [before, after] = t('Temporary password for {name}.').split('{name}');
  return (
    <div className="space-y-3 text-sm">
      <p>
        {before}
        <strong>{name}</strong>
        {after} {t('It is shown only once — share it with the employee through a secure channel.')}
      </p>
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-base">
        <span className="flex-1 select-all">{password}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          icon={<Copy className="h-4 w-4" />}
          onClick={() => {
            void navigator.clipboard?.writeText(password).then(() => setCopied(true));
          }}
        >
          {copied ? t('Copied') : t('Copy')}
        </Button>
      </div>
      <p className="text-xs text-slate-500">{t('They will be asked to choose their own password at first sign-in.')}</p>
    </div>
  );
}
